/** Central auth state — one role per browser (matches single Django session cookie). */

export const AUTH_ROLE_KEY = 'authRole';
export const USER_TOKEN_KEY = 'userToken';
export const DOCTOR_TOKEN_KEY = 'doctorToken';
export const ADMIN_TOKEN_KEY = 'adminToken';
export const USER_ID_KEY = 'userId';
export const DOCTOR_ID_KEY = 'doctorId';
export const ADMIN_ID_KEY = 'adminId';
export const AUTH_SYNC_KEY = 'fitnova_auth_sync';
export const AUTH_BOUNCE_KEY = 'fitnova_auth_bounce';

const LOGIN_PATH_PATTERNS = [
  '/login/',
  '/register/',
  '/google',
  '/send-email-otp/',
  '/verify-email-otp/',
  '/verify-2fa/',
  '/resend-2fa-otp/',
  '/forgot',
  '/reset',
  '/session-check/',
  '/logout/',
];

function broadcastAuthSync() {
  try {
    localStorage.setItem(AUTH_SYNC_KEY, String(Date.now()));
  } catch {
    /* ignore quota errors */
  }
}

export function clearAllAuthLocal({ broadcast = true } = {}) {
  const bounce = sessionStorage.getItem(AUTH_BOUNCE_KEY);
  localStorage.removeItem(USER_TOKEN_KEY);
  localStorage.removeItem(DOCTOR_TOKEN_KEY);
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(DOCTOR_ID_KEY);
  localStorage.removeItem(ADMIN_ID_KEY);
  localStorage.removeItem(AUTH_ROLE_KEY);
  localStorage.removeItem("fitnova_chats");
  localStorage.removeItem("fitnova_active_chat");
  localStorage.removeItem("fitnova_chat_titles");
  localStorage.removeItem("pinnedChats");
  sessionStorage.clear();
  if (bounce) {
    try {
      sessionStorage.setItem(AUTH_BOUNCE_KEY, bounce);
    } catch {
      /* ignore */
    }
  }
  if (broadcast) broadcastAuthSync();
}

export function markAuthBounce() {
  try {
    sessionStorage.setItem(AUTH_BOUNCE_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function clearAuthBounce() {
  try {
    sessionStorage.removeItem(AUTH_BOUNCE_KEY);
  } catch {
    /* ignore */
  }
}

export function shouldSkipAutoLogin(maxAgeMs = 20000) {
  try {
    const t = sessionStorage.getItem(AUTH_BOUNCE_KEY);
    if (!t) return false;
    return Date.now() - Number(t) < maxAgeMs;
  } catch {
    return false;
  }
}

export function setUserSession(userId) {
  clearAllAuthLocal();
  clearAuthBounce();
  localStorage.setItem(AUTH_ROLE_KEY, 'user');
  localStorage.setItem(USER_TOKEN_KEY, 'true');
  if (userId != null) {
    localStorage.setItem(USER_ID_KEY, String(userId));
  }
  broadcastAuthSync();
}

export function setDoctorSession(doctorId) {
  clearAllAuthLocal();
  clearAuthBounce();
  localStorage.setItem(AUTH_ROLE_KEY, 'doctor');
  localStorage.setItem(DOCTOR_TOKEN_KEY, 'true');
  if (doctorId != null) {
    localStorage.setItem(DOCTOR_ID_KEY, String(doctorId));
  }
  broadcastAuthSync();
}

/** Staff/superuser — same credentials as Django /admin/ */
export function setAdminSession(adminId) {
  clearAllAuthLocal();
  clearAuthBounce();
  localStorage.setItem(AUTH_ROLE_KEY, 'admin');
  localStorage.setItem(ADMIN_TOKEN_KEY, 'true');
  if (adminId != null) {
    localStorage.setItem(ADMIN_ID_KEY, String(adminId));
  }
  broadcastAuthSync();
}

export function clearUserSessionLocal() {
  localStorage.removeItem(USER_TOKEN_KEY);
  localStorage.removeItem(USER_ID_KEY);
  if (localStorage.getItem(AUTH_ROLE_KEY) === 'user') {
    localStorage.removeItem(AUTH_ROLE_KEY);
  }
  localStorage.removeItem("fitnova_chats");
  localStorage.removeItem("fitnova_active_chat");
  localStorage.removeItem("fitnova_chat_titles");
  localStorage.removeItem("pinnedChats");
}

export function clearDoctorSessionLocal() {
  localStorage.removeItem(DOCTOR_TOKEN_KEY);
  localStorage.removeItem(DOCTOR_ID_KEY);
  if (localStorage.getItem(AUTH_ROLE_KEY) === 'doctor') {
    localStorage.removeItem(AUTH_ROLE_KEY);
  }
}

export function clearAdminSessionLocal() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_ID_KEY);
  if (localStorage.getItem(AUTH_ROLE_KEY) === 'admin') {
    localStorage.removeItem(AUTH_ROLE_KEY);
  }
}

export function isUserAuthenticated() {
  return (
    localStorage.getItem(AUTH_ROLE_KEY) === 'user' &&
    !!localStorage.getItem(USER_TOKEN_KEY)
  );
}

export function isDoctorAuthenticated() {
  return (
    localStorage.getItem(AUTH_ROLE_KEY) === 'doctor' &&
    !!localStorage.getItem(DOCTOR_TOKEN_KEY)
  );
}

export function isAdminAuthenticated() {
  return (
    localStorage.getItem(AUTH_ROLE_KEY) === 'admin' &&
    !!localStorage.getItem(ADMIN_TOKEN_KEY)
  );
}

export function getAuthRole() {
  return localStorage.getItem(AUTH_ROLE_KEY);
}

export function getStoredUserId() {
  return localStorage.getItem(USER_ID_KEY);
}

export function getStoredDoctorId() {
  return localStorage.getItem(DOCTOR_ID_KEY);
}

export function isAuthApiUrl(url = '') {
  return LOGIN_PATH_PATTERNS.some((p) => url.includes(p));
}

export function handleAuthFailure(role, message, { broadcast = true } = {}) {
  const r = role || getAuthRole();
  if (message) {
    window.dispatchEvent(
      new CustomEvent('fitnova:session-expired', { detail: { role: r, message } })
    );
  }
  markAuthBounce();
  clearAllAuthLocal({ broadcast });
  if (r === 'doctor') {
    window.location.assign('/doctor/login');
  } else if (r === 'admin') {
    window.location.assign('/admin/login');
  } else {
    window.location.assign('/user/login');
  }
}

export function initAuthSyncListener() {
  if (typeof window === 'undefined') return;

  window.addEventListener('storage', (e) => {
    if (e.key !== AUTH_SYNC_KEY && e.key !== AUTH_ROLE_KEY) return;

    const path = window.location.pathname;
    const onUserArea = path.startsWith('/user/') && !path.includes('/login') && !path.includes('/register');
    const onDoctorArea = path.startsWith('/doctor/') && !path.includes('/login') && !path.includes('/register');
    const onAdminArea = path.startsWith('/admin/') && !path.includes('/login');

    if (e.key === AUTH_ROLE_KEY && e.newValue && e.oldValue && e.newValue !== e.oldValue) {
      if (e.newValue === 'user' && onDoctorArea) {
        handleAuthFailure('doctor', 'You signed in as a patient in another tab. Please log in again.', {
          broadcast: false,
        });
      } else if (e.newValue === 'doctor' && onUserArea) {
        handleAuthFailure('user', 'You signed in as a doctor in another tab. Please log in again.', {
          broadcast: false,
        });
      } else if (onAdminArea && e.newValue !== 'admin') {
        handleAuthFailure('admin', 'Admin session ended. Please log in again.', { broadcast: false });
      }
      return;
    }

    if (e.key === AUTH_SYNC_KEY && !localStorage.getItem(AUTH_ROLE_KEY)) {
      if (onUserArea) {
        handleAuthFailure('user', 'Your session ended. Please log in again.', { broadcast: false });
      } else if (onDoctorArea) {
        handleAuthFailure('doctor', 'Your session ended. Please log in again.', { broadcast: false });
      } else if (onAdminArea) {
        handleAuthFailure('admin', 'Admin session ended. Please log in again.', { broadcast: false });
      }
    }
  });
}
